import React from "react";
import Image from "next/image";

/* ---------------- TYPES ---------------- */

type ButtonType = {
  text: string;
  onClick?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  variant?: "primary" | "secondary";
};

type StrategySectionProps = {
  title: string;
  description: string;
  note?: string;
  buttons?: ButtonType[];
  descriptionWidthClass?: string;
};

/* ---------------- COMPONENT ---------------- */

export default function StrategySection({
  title,
  description,
  note,
  buttons = [],
  descriptionWidthClass = "w-[881px]",
}: StrategySectionProps) {
  const stars = [
    "/star-s-fill.svg",
    "/star-s-fill.svg",
    "/star-s-fill.svg",
    "/star-s-fill.svg",
  ];

  const primaryButton = buttons?.[0];
  const secondaryButton = buttons?.[1];

  return (
    <>
      {/* ================= DESKTOP ================= */}
      <section className="relative bg-black overflow-hidden py-32 h-[548px] hidden sm:block mt-1">
        {/* CIRCLES */}
        <div className="absolute left-1/2 top-[420px] -translate-x-1/2 pointer-events-none">
          <div className="absolute w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/2 border-2 border-dashed border-white/10 rounded-full" />
          <div className="absolute w-[987px] h-[987px] -translate-x-1/2 -translate-y-1/2 border-2 border-dashed border-white/10 rounded-full" />
          <div className="absolute sm:block hidden w-[1389px] h-[1389px] -translate-x-1/2 -translate-y-1/2 border-2 border-dashed border-white/10 rounded-full" />
        </div>

        {/* CONTENT */}
        <div className="relative max-w-[1440px] mx-auto">
          <div className="max-w-3xl mx-auto flex flex-col items-center text-center gap-8">
            <div className="space-y-4">
              <h2 className="text-[56px] leading-[110%] mt-1 font-inter font-medium tracking-tight text-white">
                {title}
              </h2>

              <p
                className={`text-white/70 ${descriptionWidthClass} mx-auto text-lg font-inter leading-6`}
              >
                {description}
              </p>
            </div>

            {/* BUTTONS (dynamic) */}
            <div className="flex flex-col sm:flex-row gap-3 items-center">
              {primaryButton &&
                (primaryButton.href ? (
                  <a
                    href={primaryButton.href}
                    target={primaryButton.target ?? "_blank"}
                    rel={primaryButton.rel ?? "noopener noreferrer"}
                    className="flex items-center gap-2 bg-[#335cff] text-white px-4 py-2 rounded-lg"
                  >
                    {primaryButton.text}
                    <span>
                      <svg
                        width="6"
                        height="10"
                        viewBox="0 0 6 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.7125 4.773L0 1.0605L1.0605 0L5.8335 4.773L1.0605 9.546L0 8.4855L3.7125 4.773Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </a>
                ) : (
                  <button
                    onClick={primaryButton.onClick}
                    className="flex items-center gap-2 bg-[#335cff] text-white px-4 py-2 rounded-lg"
                  >
                    {primaryButton.text}
                    <span>
                      <svg
                        width="6"
                        height="10"
                        viewBox="0 0 6 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.7125 4.773L0 1.0605L1.0605 0L5.8335 4.773L1.0605 9.546L0 8.4855L3.7125 4.773Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </button>
                ))}

              {secondaryButton &&
                (secondaryButton.href ? (
                  <a
                    href={secondaryButton.href}
                    target={secondaryButton.target ?? "_blank"}
                    rel={secondaryButton.rel ?? "noopener noreferrer"}
                    className="flex items-center gap-2 border border-[#6fa6ff] bg-transparent text-[#6fa6ff] px-5 py-2.5 rounded-lg text-sm font-normal hover:bg-white/5 hover:border-transparent transition"
                  >
                    <span>
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.93976 12.3705L4.48276 12.6877C5.39846 13.2218 6.43995 13.5022 7.50001 13.5C8.6867 13.5 9.84674 13.1481 10.8334 12.4888C11.8201 11.8295 12.5892 10.8925 13.0433 9.7961C13.4974 8.69974 13.6162 7.49334 13.3847 6.32946C13.1532 5.16557 12.5818 4.09647 11.7427 3.25736C10.9035 2.41824 9.83444 1.8468 8.67055 1.61529C7.50667 1.38378 6.30027 1.5026 5.20391 1.95672C4.10755 2.41085 3.17048 3.17988 2.51119 4.16658C1.8519 5.15327 1.50001 6.31331 1.50001 7.5C1.50001 8.577 1.78276 9.60975 2.31301 10.518L2.62951 11.061L2.13976 12.8618L3.93976 12.3705ZM0.00301004 15L1.01701 11.274C0.348863 10.1286 -0.00216797 8.82599 1.00742e-05 7.5C1.00742e-05 3.35775 3.35776 0 7.50001 0C11.6423 0 15 3.35775 15 7.5C15 11.6422 11.6423 15 7.50001 15C6.17459 15.0021 4.8725 14.6514 3.72751 13.9838L0.00301004 15ZM4.79326 3.981C4.89376 3.9735 4.99501 3.9735 5.09551 3.978C5.13601 3.981 5.17651 3.9855 5.21701 3.99C5.33626 4.0035 5.46751 4.07625 5.51176 4.17675C5.73526 4.68375 5.95276 5.1945 6.16276 5.70675C6.20926 5.82075 6.18151 5.967 6.09301 6.1095C6.03214 6.20586 5.96631 6.29898 5.89576 6.3885C5.81101 6.49725 5.62876 6.69675 5.62876 6.69675C5.62876 6.69675 5.55451 6.78525 5.58301 6.8955C5.59351 6.9375 5.62801 6.99825 5.65951 7.04925L5.70376 7.1205C5.89576 7.44075 6.15376 7.7655 6.46876 8.0715C6.55876 8.1585 6.64651 8.24775 6.74101 8.331C7.09201 8.64075 7.48951 8.8935 7.91851 9.081L7.92226 9.0825C7.98601 9.11025 8.01826 9.12525 8.11126 9.165C8.15776 9.1845 8.20576 9.20175 8.25451 9.2145C8.30489 9.22733 8.35794 9.22491 8.40694 9.20756C8.45595 9.1902 8.49869 9.15868 8.52976 9.117C9.07276 8.45925 9.12226 8.4165 9.12676 8.4165V8.418C9.16448 8.38282 9.2093 8.35611 9.2582 8.33968C9.3071 8.32325 9.35895 8.31748 9.41026 8.32275C9.45526 8.32575 9.50101 8.334 9.54301 8.35275C9.94126 8.535 10.593 8.81925 10.593 8.81925L11.0295 9.015C11.103 9.05025 11.1698 9.1335 11.172 9.21375C11.175 9.264 11.1795 9.345 11.1623 9.4935C11.1383 9.68775 11.0798 9.921 11.0213 10.0432C10.9812 10.1267 10.928 10.2031 10.8638 10.2697C10.7881 10.3492 10.7052 10.4215 10.6163 10.4857C10.5855 10.5089 10.5542 10.5314 10.5225 10.5532C10.4292 10.6124 10.3334 10.6675 10.2353 10.7183C10.0422 10.8208 9.82888 10.8797 9.61051 10.8907C9.47176 10.8982 9.33301 10.9087 9.19351 10.9012C9.18751 10.9012 8.76751 10.836 8.76751 10.836C7.7012 10.5555 6.71505 10.0301 5.88751 9.3015C5.71801 9.15225 5.56126 8.99175 5.40076 8.832C4.73326 8.16825 4.22926 7.452 3.92326 6.7755C3.76664 6.44347 3.68203 6.08205 3.67501 5.715C3.67192 5.25964 3.82077 4.81625 4.09801 4.455C4.15276 4.3845 4.20451 4.311 4.29376 4.22625C4.38901 4.13625 4.44901 4.08825 4.51426 4.05525C4.60102 4.01177 4.69566 3.98626 4.79251 3.98025L4.79326 3.981Z"
                          fill="#6fa6ff"
                        />
                      </svg>
                    </span>
                    {secondaryButton.text}
                  </a>
                ) : (
                  <button
                    onClick={secondaryButton.onClick}
                    className="flex items-center gap-2 border border-[#6fa6ff] bg-transparent text-[#6fa6ff] px-5 py-2.5 rounded-lg text-sm font-normal hover:bg-white/5 hover:border-transparent transition"
                  >
                    <span>
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.93976 12.3705L4.48276 12.6877C5.39846 13.2218 6.43995 13.5022 7.50001 13.5C8.6867 13.5 9.84674 13.1481 10.8334 12.4888C11.8201 11.8295 12.5892 10.8925 13.0433 9.7961C13.4974 8.69974 13.6162 7.49334 13.3847 6.32946C13.1532 5.16557 12.5818 4.09647 11.7427 3.25736C10.9035 2.41824 9.83444 1.8468 8.67055 1.61529C7.50667 1.38378 6.30027 1.5026 5.20391 1.95672C4.10755 2.41085 3.17048 3.17988 2.51119 4.16658C1.8519 5.15327 1.50001 6.31331 1.50001 7.5C1.50001 8.577 1.78276 9.60975 2.31301 10.518L2.62951 11.061L2.13976 12.8618L3.93976 12.3705ZM0.00301004 15L1.01701 11.274C0.348863 10.1286 -0.00216797 8.82599 1.00742e-05 7.5C1.00742e-05 3.35775 3.35776 0 7.50001 0C11.6423 0 15 3.35775 15 7.5C15 11.6422 11.6423 15 7.50001 15C6.17459 15.0021 4.8725 14.6514 3.72751 13.9838L0.00301004 15ZM4.79326 3.981C4.89376 3.9735 4.99501 3.9735 5.09551 3.978C5.13601 3.981 5.17651 3.9855 5.21701 3.99C5.33626 4.0035 5.46751 4.07625 5.51176 4.17675C5.73526 4.68375 5.95276 5.1945 6.16276 5.70675C6.20926 5.82075 6.18151 5.967 6.09301 6.1095C6.03214 6.20586 5.96631 6.29898 5.89576 6.3885C5.81101 6.49725 5.62876 6.69675 5.62876 6.69675C5.62876 6.69675 5.55451 6.78525 5.58301 6.8955C5.59351 6.9375 5.62801 6.99825 5.65951 7.04925L5.70376 7.1205C5.89576 7.44075 6.15376 7.7655 6.46876 8.0715C6.55876 8.1585 6.64651 8.24775 6.74101 8.331C7.09201 8.64075 7.48951 8.8935 7.91851 9.081L7.92226 9.0825C7.98601 9.11025 8.01826 9.12525 8.11126 9.165C8.15776 9.1845 8.20576 9.20175 8.25451 9.2145C8.30489 9.22733 8.35794 9.22491 8.40694 9.20756C8.45595 9.1902 8.49869 9.15868 8.52976 9.117C9.07276 8.45925 9.12226 8.4165 9.12676 8.4165V8.418C9.16448 8.38282 9.2093 8.35611 9.2582 8.33968C9.3071 8.32325 9.35895 8.31748 9.41026 8.32275C9.45526 8.32575 9.50101 8.334 9.54301 8.35275C9.94126 8.535 10.593 8.81925 10.593 8.81925L11.0295 9.015C11.103 9.05025 11.1698 9.1335 11.172 9.21375C11.175 9.264 11.1795 9.345 11.1623 9.4935C11.1383 9.68775 11.0798 9.921 11.0213 10.0432C10.9812 10.1267 10.928 10.2031 10.8638 10.2697C10.7881 10.3492 10.7052 10.4215 10.6163 10.4857C10.5855 10.5089 10.5542 10.5314 10.5225 10.5532C10.4292 10.6124 10.3334 10.6675 10.2353 10.7183C10.0422 10.8208 9.82888 10.8797 9.61051 10.8907C9.47176 10.8982 9.33301 10.9087 9.19351 10.9012C9.18751 10.9012 8.76751 10.836 8.76751 10.836C7.7012 10.5555 6.71505 10.0301 5.88751 9.3015C5.71801 9.15225 5.56126 8.99175 5.40076 8.832C4.73326 8.16825 4.22926 7.452 3.92326 6.7755C3.76664 6.44347 3.68203 6.08205 3.67501 5.715C3.67192 5.25964 3.82077 4.81625 4.09801 4.455C4.15276 4.3845 4.20451 4.311 4.29376 4.22625C4.38901 4.13625 4.44901 4.08825 4.51426 4.05525C4.60102 4.01177 4.69566 3.98626 4.79251 3.98025L4.79326 3.981Z"
                          fill="#6fa6ff"
                        />
                      </svg>
                    </span>
                    {secondaryButton.text}
                  </button>
                ))}
            </div>

            {/* NOTE */}
            {note && (
              <p className="text-[14px] font-medium text-white/70">{note}</p>
            )}
          </div>
        </div>

        {/* AVATARS (unchanged) */}
        <div className="absolute top-[120px] left-[calc(50%-418px)] -translate-y-1/2">
          <Image
            src="/men-avatar.png"
            alt=""
            width={50}
            height={50}
            className="rounded-full border-2 border-white"
          />
        </div>

        <div className="absolute bottom-[80px] left-[calc(50%-518px)] -translate-y-1/2">
          <Image
            src="/footer-avatar-1.svg"
            alt=""
            width={50}
            height={50}
            className="rounded-full border-2 border-white"
          />
        </div>

        <div className="absolute top-[120px] left-[calc(50%+368px)] -translate-y-1/2">
          <Image
            src="/footer-avatar-1.svg"
            alt=""
            width={50}
            height={50}
            className="rounded-full border-2 border-white"
          />
        </div>

        <div className="absolute bottom-[80px] left-[calc(50%+420px)] -translate-y-1/2">
          <div className="flex flex-col items-center gap-2">
            <Image
              src={"/footer-avatar-4.png"}
              width={50}
              height={50}
              alt="avatar"
            />

            <div className="bg-[#0f1218] border border-white/10 rounded-full px-3 py-2 items-center gap-2 shadow lg:flex hidden">
              <div className="flex items-center gap-1 text-xs text-white/60">
                {stars.map((s, i) => (
                  <Image key={i} src={s} alt="star" width={12} height={12} />
                ))}

                <span>4.5/5.0</span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute top-[60px] left-[calc(40%-380px)] -translate-y-1/2">
          <p className="text-white/70 text-sm font-medium w-[200px]">
            We’ll help meet your Business goals.
          </p>
        </div>
      </section>

      {/* ================= MOBILE ================= */}
      <section className="relative bg-black flex overflow-hidden py-[55px] h-[548px] items-center sm:hidden">
        {/* CIRCLES */}
        <div className="absolute left-1/2 top-[280px] -translate-x-1/2 pointer-events-none">
          <div className="absolute w-[300px] h-[300px] -translate-x-1/2 -translate-y-1/2 border-2 border-dashed border-white/10 rounded-full" />
          <div className="absolute w-[500px] h-[500px] -translate-x-1/2 -translate-y-1/2 border-2 border-dashed border-white/10 rounded-full" />
        </div>

        {/* CONTENT */}
        <div className="relative max-w-[1440px] mx-auto">
          <div className="max-w-3xl mx-auto flex flex-col items-center text-center gap-8">
            <h2 className="text-[26px] font-medium text-white">{title}</h2>

            <p className="text-white/70 text-[18px] max-w-[371px] mx-auto">
              {description}
            </p>
            <div className="flex-col justify-center gap-2 flex">
              {primaryButton &&
                (primaryButton.href ? (
                  <a
                    href={primaryButton.href}
                    target={primaryButton.target ?? "_blank"}
                    rel={primaryButton.rel ?? "noopener noreferrer"}
                    className="flex items-center gap-2 bg-[#335cff] text-white px-4 py-2 rounded-lg"
                  >
                    {primaryButton.text}
                    <span>
                      <svg
                        width="6"
                        height="10"
                        viewBox="0 0 6 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.7125 4.773L0 1.0605L1.0605 0L5.8335 4.773L1.0605 9.546L0 8.4855L3.7125 4.773Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </a>
                ) : (
                  <button
                    onClick={primaryButton.onClick}
                    className="flex items-center gap-2 bg-[#335cff] text-white px-4 py-2 rounded-lg"
                  >
                    {primaryButton.text}
                    <span>
                      <svg
                        width="6"
                        height="10"
                        viewBox="0 0 6 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.7125 4.773L0 1.0605L1.0605 0L5.8335 4.773L1.0605 9.546L0 8.4855L3.7125 4.773Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </button>
                ))}

              {secondaryButton &&
                (secondaryButton.href ? (
                  <a
                    href={secondaryButton.href}
                    target={secondaryButton.target ?? "_blank"}
                    rel={secondaryButton.rel ?? "noopener noreferrer"}
                    className="flex items-center gap-2 border border-[#6fa6ff] bg-transparent text-[#6fa6ff] px-5 py-2.5 rounded-lg text-sm font-normal hover:bg-white/5 hover:border-transparent transition"
                  >
                    <span>
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.93976 12.3705L4.48276 12.6877C5.39846 13.2218 6.43995 13.5022 7.50001 13.5C8.6867 13.5 9.84674 13.1481 10.8334 12.4888C11.8201 11.8295 12.5892 10.8925 13.0433 9.7961C13.4974 8.69974 13.6162 7.49334 13.3847 6.32946C13.1532 5.16557 12.5818 4.09647 11.7427 3.25736C10.9035 2.41824 9.83444 1.8468 8.67055 1.61529C7.50667 1.38378 6.30027 1.5026 5.20391 1.95672C4.10755 2.41085 3.17048 3.17988 2.51119 4.16658C1.8519 5.15327 1.50001 6.31331 1.50001 7.5C1.50001 8.577 1.78276 9.60975 2.31301 10.518L2.62951 11.061L2.13976 12.8618L3.93976 12.3705ZM0.00301004 15L1.01701 11.274C0.348863 10.1286 -0.00216797 8.82599 1.00742e-05 7.5C1.00742e-05 3.35775 3.35776 0 7.50001 0C11.6423 0 15 3.35775 15 7.5C15 11.6422 11.6423 15 7.50001 15C6.17459 15.0021 4.8725 14.6514 3.72751 13.9838L0.00301004 15ZM4.79326 3.981C4.89376 3.9735 4.99501 3.9735 5.09551 3.978C5.13601 3.981 5.17651 3.9855 5.21701 3.99C5.33626 4.0035 5.46751 4.07625 5.51176 4.17675C5.73526 4.68375 5.95276 5.1945 6.16276 5.70675C6.20926 5.82075 6.18151 5.967 6.09301 6.1095C6.03214 6.20586 5.96631 6.29898 5.89576 6.3885C5.81101 6.49725 5.62876 6.69675 5.62876 6.69675C5.62876 6.69675 5.55451 6.78525 5.58301 6.8955C5.59351 6.9375 5.62801 6.99825 5.65951 7.04925L5.70376 7.1205C5.89576 7.44075 6.15376 7.7655 6.46876 8.0715C6.55876 8.1585 6.64651 8.24775 6.74101 8.331C7.09201 8.64075 7.48951 8.8935 7.91851 9.081L7.92226 9.0825C7.98601 9.11025 8.01826 9.12525 8.11126 9.165C8.15776 9.1845 8.20576 9.20175 8.25451 9.2145C8.30489 9.22733 8.35794 9.22491 8.40694 9.20756C8.45595 9.1902 8.49869 9.15868 8.52976 9.117C9.07276 8.45925 9.12226 8.4165 9.12676 8.4165V8.418C9.16448 8.38282 9.2093 8.35611 9.2582 8.33968C9.3071 8.32325 9.35895 8.31748 9.41026 8.32275C9.45526 8.32575 9.50101 8.334 9.54301 8.35275C9.94126 8.535 10.593 8.81925 10.593 8.81925L11.0295 9.015C11.103 9.05025 11.1698 9.1335 11.172 9.21375C11.175 9.264 11.1795 9.345 11.1623 9.4935C11.1383 9.68775 11.0798 9.921 11.0213 10.0432C10.9812 10.1267 10.928 10.2031 10.8638 10.2697C10.7881 10.3492 10.7052 10.4215 10.6163 10.4857C10.5855 10.5089 10.5542 10.5314 10.5225 10.5532C10.4292 10.6124 10.3334 10.6675 10.2353 10.7183C10.0422 10.8208 9.82888 10.8797 9.61051 10.8907C9.47176 10.8982 9.33301 10.9087 9.19351 10.9012C9.18751 10.9012 8.76751 10.836 8.76751 10.836C7.7012 10.5555 6.71505 10.0301 5.88751 9.3015C5.71801 9.15225 5.56126 8.99175 5.40076 8.832C4.73326 8.16825 4.22926 7.452 3.92326 6.7755C3.76664 6.44347 3.68203 6.08205 3.67501 5.715C3.67192 5.25964 3.82077 4.81625 4.09801 4.455C4.15276 4.3845 4.20451 4.311 4.29376 4.22625C4.38901 4.13625 4.44901 4.08825 4.51426 4.05525C4.60102 4.01177 4.69566 3.98626 4.79251 3.98025L4.79326 3.981Z"
                          fill="#6fa6ff"
                        />
                      </svg>
                    </span>
                    {secondaryButton.text}
                  </a>
                ) : (
                  <button
                    onClick={secondaryButton.onClick}
                    className="flex items-center gap-2 border border-[#6fa6ff] bg-transparent text-[#6fa6ff] px-5 py-2.5 rounded-lg text-sm font-normal hover:bg-white/5 hover:border-transparent transition"
                  >
                    <span>
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.93976 12.3705L4.48276 12.6877C5.39846 13.2218 6.43995 13.5022 7.50001 13.5C8.6867 13.5 9.84674 13.1481 10.8334 12.4888C11.8201 11.8295 12.5892 10.8925 13.0433 9.7961C13.4974 8.69974 13.6162 7.49334 13.3847 6.32946C13.1532 5.16557 12.5818 4.09647 11.7427 3.25736C10.9035 2.41824 9.83444 1.8468 8.67055 1.61529C7.50667 1.38378 6.30027 1.5026 5.20391 1.95672C4.10755 2.41085 3.17048 3.17988 2.51119 4.16658C1.8519 5.15327 1.50001 6.31331 1.50001 7.5C1.50001 8.577 1.78276 9.60975 2.31301 10.518L2.62951 11.061L2.13976 12.8618L3.93976 12.3705ZM0.00301004 15L1.01701 11.274C0.348863 10.1286 -0.00216797 8.82599 1.00742e-05 7.5C1.00742e-05 3.35775 3.35776 0 7.50001 0C11.6423 0 15 3.35775 15 7.5C15 11.6422 11.6423 15 7.50001 15C6.17459 15.0021 4.8725 14.6514 3.72751 13.9838L0.00301004 15ZM4.79326 3.981C4.89376 3.9735 4.99501 3.9735 5.09551 3.978C5.13601 3.981 5.17651 3.9855 5.21701 3.99C5.33626 4.0035 5.46751 4.07625 5.51176 4.17675C5.73526 4.68375 5.95276 5.1945 6.16276 5.70675C6.20926 5.82075 6.18151 5.967 6.09301 6.1095C6.03214 6.20586 5.96631 6.29898 5.89576 6.3885C5.81101 6.49725 5.62876 6.69675 5.62876 6.69675C5.62876 6.69675 5.55451 6.78525 5.58301 6.8955C5.59351 6.9375 5.62801 6.99825 5.65951 7.04925L5.70376 7.1205C5.89576 7.44075 6.15376 7.7655 6.46876 8.0715C6.55876 8.1585 6.64651 8.24775 6.74101 8.331C7.09201 8.64075 7.48951 8.8935 7.91851 9.081L7.92226 9.0825C7.98601 9.11025 8.01826 9.12525 8.11126 9.165C8.15776 9.1845 8.20576 9.20175 8.25451 9.2145C8.30489 9.22733 8.35794 9.22491 8.40694 9.20756C8.45595 9.1902 8.49869 9.15868 8.52976 9.117C9.07276 8.45925 9.12226 8.4165 9.12676 8.4165V8.418C9.16448 8.38282 9.2093 8.35611 9.2582 8.33968C9.3071 8.32325 9.35895 8.31748 9.41026 8.32275C9.45526 8.32575 9.50101 8.334 9.54301 8.35275C9.94126 8.535 10.593 8.81925 10.593 8.81925L11.0295 9.015C11.103 9.05025 11.1698 9.1335 11.172 9.21375C11.175 9.264 11.1795 9.345 11.1623 9.4935C11.1383 9.68775 11.0798 9.921 11.0213 10.0432C10.9812 10.1267 10.928 10.2031 10.8638 10.2697C10.7881 10.3492 10.7052 10.4215 10.6163 10.4857C10.5855 10.5089 10.5542 10.5314 10.5225 10.5532C10.4292 10.6124 10.3334 10.6675 10.2353 10.7183C10.0422 10.8208 9.82888 10.8797 9.61051 10.8907C9.47176 10.8982 9.33301 10.9087 9.19351 10.9012C9.18751 10.9012 8.76751 10.836 8.76751 10.836C7.7012 10.5555 6.71505 10.0301 5.88751 9.3015C5.71801 9.15225 5.56126 8.99175 5.40076 8.832C4.73326 8.16825 4.22926 7.452 3.92326 6.7755C3.76664 6.44347 3.68203 6.08205 3.67501 5.715C3.67192 5.25964 3.82077 4.81625 4.09801 4.455C4.15276 4.3845 4.20451 4.311 4.29376 4.22625C4.38901 4.13625 4.44901 4.08825 4.51426 4.05525C4.60102 4.01177 4.69566 3.98626 4.79251 3.98025L4.79326 3.981Z"
                          fill="#6fa6ff"
                        />
                      </svg>
                    </span>
                    {secondaryButton.text}
                  </button>
                ))}
            </div>
          </div>
        </div>

        {/* AVATARS (unchanged) */}
        <div className="absolute top-[190px] left-10">
          <Image
            src="/men-avatar.png"
            alt=""
            width={50}
            height={50}
            className="rounded-full border-2 border-white"
          />
        </div>

        <div className="absolute top-[80px] right-6">
          <Image
            src="/footer-avatar-1.svg"
            alt=""
            width={50}
            height={50}
            className="rounded-full border-2 border-white"
          />
        </div>

        <div className="absolute bottom-[20px] left-6">
          <Image
            src="/footer-avatar-4.png"
            alt=""
            width={50}
            height={50}
            className="rounded-full border-2 border-white"
          />
        </div>
      </section>
    </>
  );
}
